// client/src/components/recursos/FiltroRecursos.js - VERSÃO 100% PADRONIZADA
import React, { useState } from 'react';
import { useCategoriasParaFormulario } from '../../hooks/useCategorias';
import { Search, X, Filter, Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';

const FiltroRecursos = ({ onApplyFilters, loading, accountId }) => {
    // Hooks de dados
    const { data: categoriasData, isLoading: loadingCategorias } = useCategoriasParaFormulario(accountId);
    
    // Processamento de dados
    const categorias = Array.isArray(categoriasData) ? categoriasData.filter(categoria => 
        categoria.tipo === 'recurso' || categoria.tipo === 'geral'
    ) : [];

    // Estado inicial
    const initialFilters = {
        search: '',
        nome: '',
        tipo: '',
        categoria: '',
        status: '',
        disponivelPara: '',
        tipoItem: '',
        movel: '',
        disponivelOnline: ''
    };

    const [filters, setFilters] = useState(initialFilters);

    // Handlers
    const onChange = e => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const onSubmit = e => {
        e.preventDefault();
        
        const activeFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, v]) => {
                if (v === '' || v === null || v === undefined) return false;
                if (typeof v === 'string' && v.trim() === '') return false;
                return true;
            })
        );
        
        onApplyFilters(activeFilters);
    };

    const onClear = () => {
        setFilters(initialFilters);
        onApplyFilters({});
    };

    const hasActiveFilters = Object.values(filters).some(value => 
        value !== '' && value !== null && value !== undefined
    );

    return (
        <form onSubmit={onSubmit} className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-end">
                
                {/* 1. Busca Geral */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="search" className="text-xs font-semibold text-gray-600 uppercase">Busca Geral</label>
                    <input 
                        type="text" 
                        name="search" 
                        id="search"
                        placeholder="Nome, descrição..." 
                        value={filters.search} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                </div>

                {/* 2. Nome do Recurso */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="nome" className="text-xs font-semibold text-gray-600 uppercase">Nome Específico</label>
                    <input 
                        type="text" 
                        name="nome" 
                        id="nome"
                        placeholder="Nome exato..." 
                        value={filters.nome} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                </div>

                {/* 3. Tipo de Recurso */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="tipo" className="text-xs font-semibold text-gray-600 uppercase">Tipo</label>
                    <select 
                        name="tipo" 
                        id="tipo" 
                        value={filters.tipo} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos</option>
                        <option value="Sala">Sala</option>
                        <option value="Equipamento">Equipamento</option>
                        <option value="Maca ou Cadeira">Maca ou Cadeira</option>
                        <option value="Veiculo">Veículo</option>
                        <option value="Outros">Outros</option>
                    </select>
                </div>

                {/* 4. Categoria */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="categoria" className="text-xs font-semibold text-gray-600 uppercase">Categoria</label>
                    <select 
                        name="categoria" 
                        id="categoria" 
                        value={filters.categoria} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        disabled={loadingCategorias}
                    >
                        <option value="">Todas as categorias</option>
                        {loadingCategorias ? (
                            <option value="" disabled>Carregando...</option>
                        ) : categorias.length > 0 ? (
                            categorias.map(categoria => (
                                <option key={categoria._id} value={categoria._id}>
                                    {categoria.nome} {categoria.tipo && `(${categoria.tipo})`}
                                </option>
                            ))
                        ) : (
                            <option value="" disabled>Nenhuma disponível</option>
                        )}
                    </select>
                </div>

                {/* 5. Status */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="status" className="text-xs font-semibold text-gray-600 uppercase">Status</label>
                    <select 
                        name="status" 
                        id="status" 
                        value={filters.status} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos</option>
                        <option value="Ativo">Ativo</option>
                        <option value="Inativo">Inativo</option>
                        <option value="Em Manutenção">Em Manutenção</option>
                        <option value="Bloqueado">Bloqueado</option>
                        <option value="Fora de Serviço">Fora de Serviço</option>
                    </select>
                </div>

                {/* 6. Disponibilidade */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="disponivelPara" className="text-xs font-semibold text-gray-600 uppercase">Uso</label>
                    <select 
                        name="disponivelPara" 
                        id="disponivelPara" 
                        value={filters.disponivelPara} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos</option>
                        <option value="INTERNO_SERVICOS">Serviços Internos</option>
                        <option value="INTERNO_LOCACAO">Locação Interna</option>
                        <option value="EXTERNO_SERVICOS">Serviços Externos</option>
                        <option value="EXTERNO_LOCACAO">Locação Externa</option>
                    </select>
                </div>

                {/* 7. Tipo de Item */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="tipoItem" className="text-xs font-semibold text-gray-600 uppercase">Origem</label>
                    <select 
                        name="tipoItem" 
                        id="tipoItem" 
                        value={filters.tipoItem} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todas</option>
                        <option value="interno">Interno</option>
                        <option value="externo">Externo</option>
                    </select>
                </div>

                {/* 8. Móvel */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="movel" className="text-xs font-semibold text-gray-600 uppercase">Móvel?</label>
                    <select 
                        name="movel" 
                        id="movel" 
                        value={filters.movel} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos</option>
                        <option value="true">Sim</option>
                        <option value="false">Não</option>
                    </select>
                </div>

                {/* 9. Online */}
                <div className="flex flex-col gap-1">
                    <label htmlFor="disponivelOnline" className="text-xs font-semibold text-gray-600 uppercase">Online?</label>
                    <select 
                        name="disponivelOnline" 
                        id="disponivelOnline" 
                        value={filters.disponivelOnline} 
                        onChange={onChange} 
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    >
                        <option value="">Todos</option>
                        <option value="true">Sim</option>
                        <option value="false">Não</option>
                    </select>
                </div>

                {/* Botões de Ação — antes reinventava cor do zero em Tailwind
                    (bg-blue-600) e o texto "Filtrar" so aparecia acima de 640px
                    (hidden sm:inline); mesmo padrao ja corrigido nos outros
                    filtros do app */}
                <div className="flex gap-2 h-full items-end">
                    <button
                        type="submit"
                        disabled={loading}
                        className="action-button action-button-primary flex-1 justify-center"
                        title="Aplicar Filtros"
                    >
                        {loading ? <Loader2 size={18} className="icon animate-spin" /> : <Search size={18} className="icon" />}
                        Aplicar Filtros
                    </button>

                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={onClear}
                            disabled={loading}
                            className="action-button action-button-secondary justify-center"
                            title="Limpar Filtros"
                        >
                            <X size={18} className="icon" />
                        </button>
                    )}
                </div>
            </div>

            {/* Resumo de Filtros */}
            {hasActiveFilters && (
                <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                        <Filter size={14} className="text-blue-600" />
                        <span>Filtros ativos:</span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                            {Object.values(filters).filter(v => v !== '').length}
                        </span>
                    </div>
                    <button onClick={onClear} className="text-red-600 hover:text-red-800 hover:underline">
                        Limpar todos
                    </button>
                </div>
            )}
        </form>
    );
};

FiltroRecursos.propTypes = {
    onApplyFilters: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    accountId: PropTypes.string
};

FiltroRecursos.defaultProps = {
    loading: false
};

export default FiltroRecursos;